import PropTypes from 'prop-types';
import { useState } from 'react';
import { useLocation, matchPath } from 'react-router-dom';

// material-ui
import Collapse from '@mui/material/Collapse';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';

// project imports
import NavItem from './NavItem';
import { useGetMenuMaster } from 'api/menu';
import { authService } from 'api/services';

// icons
import { DownOutlined, UpOutlined } from '@ant-design/icons';

// ==============================|| NAVIGATION - COLLAPSE ||============================== //

export default function NavCollapse({ item, level }) {
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const { pathname } = useLocation();

  // 하위 메뉴 중 현재 경로가 있으면 초기에 열린 상태로
  const isChildActive = item.children?.some((child) => {
    if (child.url) {
      return !!matchPath({ path: child.url, end: false }, pathname);
    }
    return false;
  });

  const [open, setOpen] = useState(isChildActive);

  const handleClick = () => {
    setOpen(!open);
  };

  const Icon = item.icon;
  const itemIcon = item.icon ? (
    <Icon style={{ fontSize: drawerOpen ? '1rem' : '1.25rem' }} />
  ) : null;

  const textColor = 'text.primary';
  const iconSelectedColor = 'primary.main';

  const currentUser = authService.getCurrentUser();
  const userRole = currentUser?.role;

  const navCollapse = item.children
    ?.filter((menuItem) => {
      if (menuItem.roles && !menuItem.roles.includes(userRole)) return false;
      return true;
    })
    .map((menuItem) => {
      switch (menuItem.type) {
        case 'item':
          return <NavItem key={menuItem.id} item={menuItem} level={level + 1} />;
        case 'collapse':
          return <NavCollapse key={menuItem.id} item={menuItem} level={level + 1} />;
        default:
          return (
            <Typography key={menuItem.id} variant="h6" color="error" align="center">
              Fix - Collapse or Item
            </Typography>
          );
      }
    });

  return (
    <>
      <ListItemButton
        disabled={item.disabled}
        onClick={handleClick}
        selected={isChildActive}
        sx={{
          zIndex: 1201,
          pl: drawerOpen ? `${level * 28}px` : 1.5,
          py: !drawerOpen && level === 1 ? 1.25 : 1,
          ...(drawerOpen && {
            '&:hover': { bgcolor: 'primary.lighter' },
            '&.Mui-selected': {
              bgcolor: 'transparent',
              color: iconSelectedColor,
              '&:hover': { bgcolor: 'primary.lighter' }
            }
          }),
          ...(!drawerOpen && {
            '&:hover': { bgcolor: 'transparent' },
            '&.Mui-selected': { '&:hover': { bgcolor: 'transparent' }, bgcolor: 'transparent' }
          })
        }}
      >
        {itemIcon && (
          <ListItemIcon
            sx={{
              minWidth: 28,
              color: isChildActive ? iconSelectedColor : textColor,
              ...(!drawerOpen && {
                borderRadius: 1.5,
                width: 36,
                height: 36,
                alignItems: 'center',
                justifyContent: 'center',
                '&:hover': { bgcolor: 'secondary.lighter' }
              }),
              ...(!drawerOpen &&
                isChildActive && {
                  bgcolor: 'primary.lighter',
                  '&:hover': { bgcolor: 'primary.lighter' }
                })
            }}
          >
            {itemIcon}
          </ListItemIcon>
        )}
        {(drawerOpen || (!drawerOpen && level !== 1)) && (
          <ListItemText
            primary={
              <Typography variant="h6" sx={{ color: isChildActive ? iconSelectedColor : textColor }}>
                {item.title}
              </Typography>
            }
          />
        )}
        {drawerOpen && (open ? <UpOutlined style={{ fontSize: '0.75rem' }} /> : <DownOutlined style={{ fontSize: '0.75rem' }} />)}
      </ListItemButton>
      {drawerOpen && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding sx={{ bgcolor: 'grey.50' }}>
            {navCollapse}
          </List>
        </Collapse>
      )}
    </>
  );
}

NavCollapse.propTypes = {
  item: PropTypes.object,
  level: PropTypes.number
};
